import { useEffect, useRef, useState } from 'react'
import { api } from '../data/api'
import { GST_STATES } from '../data/gstStates'

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--text-3)' }}>{label}</label>
      {children}
    </div>
  )
}

function Input({ value, onChange, placeholder, maxLength }) {
  return (
    <input value={value} placeholder={placeholder} maxLength={maxLength}
      onChange={e => onChange(e.target.value)}
      className="w-full h-10 px-3 rounded-xl text-sm outline-none transition-all"
      style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)' }}
      onFocus={e => e.currentTarget.style.borderColor = 'var(--brand)'}
      onBlur={e => e.currentTarget.style.borderColor = 'var(--border)'} />
  )
}

// A native <select>'s open option-list height is drawn by the OS/browser, not
// CSS — appearance-none only fixes the closed control, the popup itself can
// still render very tall for a ~36-item list. This is a custom dropdown
// instead, so the open panel can be capped with max-h + overflow-y-auto.
function StateSelect({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef(null)
  const searchRef = useRef(null)

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  useEffect(() => {
    if (open) { setSearch(''); setTimeout(() => searchRef.current?.focus(), 0) }
  }, [open])

  const filtered = GST_STATES.filter(s => s.toLowerCase().includes(search.trim().toLowerCase()))

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full h-10 px-3 rounded-xl text-sm outline-none cursor-pointer flex items-center justify-between"
        style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: value ? 'var(--text-1)' : 'var(--text-3)' }}>
        <span className="truncate">{value || 'Select state…'}</span>
        <svg className="flex-shrink-0 ml-2" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2.5 4.5L6 8L9.5 4.5" stroke="var(--text-3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-10 left-0 right-0 mt-1 rounded-xl shadow-lg overflow-hidden"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
          <div className="p-1.5" style={{ borderBottom: '1px solid var(--border)' }}>
            <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search state…"
              className="w-full h-8 px-2.5 rounded-lg text-sm outline-none"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
          </div>
          <div className="max-h-40 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm" style={{ color: 'var(--text-3)' }}>No states match "{search}".</p>
            ) : filtered.map(s => (
              <button key={s} type="button" onClick={() => { onChange(s); setOpen(false) }}
                className="w-full text-left px-3 py-2 text-sm"
                style={{ color: 'var(--text-1)', background: s === value ? 'var(--surface)' : 'transparent' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
                onMouseLeave={e => e.currentTarget.style.background = s === value ? 'var(--surface)' : 'transparent'}>
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Reused both inline (inside the buy-credits flow, when billingDetails.address.state
// is missing) and as a standalone "Billing Details" section on the profile page.
// At least one of GSTIN/PAN is required — both existing on the same buyer would
// be unusual, but a real tax document needs to identify the buyer by one of them.
export default function BillingDetailsForm({ user, updateUser, onSaved, showToast }) {
  const bd = user.billingDetails ?? {}
  const [legalName, setLegalName] = useState(bd.legalName || '')
  const [gstin,      setGstin]     = useState(bd.gstin || '')
  const [pan,        setPan]       = useState(bd.pan || '')
  const [line1,      setLine1]     = useState(bd.address?.line1 || '')
  const [line2,      setLine2]     = useState(bd.address?.line2 || '')
  const [city,       setCity]      = useState(bd.address?.city || '')
  const [state,      setState]     = useState(bd.address?.state || '')
  const [pincode,    setPincode]   = useState(bd.address?.pincode || '')
  const [saving,     setSaving]    = useState(false)
  // Field-scoped errors — shown right under the field they're about, instead
  // of one generic message the user has to match back to a field themselves.
  // `general` is the fallback for anything that isn't about a specific field
  // (e.g. a network failure).
  const [fieldErrors, setFieldErrors] = useState({})

  async function handleSave() {
    if (!state) return setFieldErrors({ state: 'State is required.' })
    if (!gstin.trim() && !pan.trim()) return setFieldErrors({ gstinPan: 'Enter at least one of GSTIN or PAN.' })
    setFieldErrors({}); setSaving(true)
    try {
      const updated = await api.patch('/api/users/me', {
        billingDetails: {
          legalName: legalName.trim() || undefined,
          gstin: gstin.trim() || undefined,
          pan: pan.trim() || undefined,
          address: { line1, line2, city, state, pincode },
        },
      })
      updateUser(updated)
      showToast?.('Billing details saved.')
      onSaved?.(updated)
    } catch (err) {
      const msg = err.message || 'Failed to save billing details.'
      if (/gstin/i.test(msg))      setFieldErrors({ gstin: msg })
      else if (/\bpan\b/i.test(msg)) setFieldErrors({ pan: msg })
      else if (/state/i.test(msg))  setFieldErrors({ state: msg })
      else                           setFieldErrors({ general: msg })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <Field label="Legal / Billing Name (optional)">
        <Input value={legalName} onChange={setLegalName} placeholder={user.name} maxLength={150} />
      </Field>
      <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
        <div className="flex-1">
          <Field label="GSTIN">
            <Input value={gstin} onChange={v => setGstin(v.toUpperCase())} placeholder="e.g. 32AANCR4696K1ZQ" maxLength={15} />
          </Field>
          {fieldErrors.gstin && <p className="text-xs mt-1" style={{ color: 'var(--red)' }}>{fieldErrors.gstin}</p>}
        </div>
        <div className="text-center sm:mt-8">
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-3)' }}>or</span>
        </div>
        <div className="flex-1">
          <Field label="PAN">
            <Input value={pan} onChange={v => setPan(v.toUpperCase())} placeholder="e.g. AANCR4696K" maxLength={10} />
          </Field>
          {fieldErrors.pan && <p className="text-xs mt-1" style={{ color: 'var(--red)' }}>{fieldErrors.pan}</p>}
        </div>
      </div>
      <p className="text-xs -mt-2" style={{ color: fieldErrors.gstinPan ? 'var(--red)' : 'var(--text-3)' }}>
        {fieldErrors.gstinPan || 'At least one of GSTIN or PAN is required.'}
      </p>
      <Field label="Address Line 1">
        <Input value={line1} onChange={setLine1} maxLength={150} />
      </Field>
      <Field label="Address Line 2 (optional)">
        <Input value={line2} onChange={setLine2} maxLength={150} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="City">
          <Input value={city} onChange={setCity} maxLength={80} />
        </Field>
        <Field label="Pincode">
          <Input value={pincode} onChange={v => setPincode(v.replace(/\D/g, ''))} maxLength={6} />
        </Field>
      </div>
      <div>
        <Field label="State *">
          <StateSelect value={state} onChange={setState} />
        </Field>
        {fieldErrors.state && <p className="text-xs mt-1" style={{ color: 'var(--red)' }}>{fieldErrors.state}</p>}
      </div>
      {fieldErrors.general && <p className="text-xs" style={{ color: 'var(--red)' }}>{fieldErrors.general}</p>}
      <div className="flex justify-end pt-1">
        <button onClick={handleSave} disabled={saving || !state || (!gstin.trim() && !pan.trim())} className="btn-primary text-sm">
          {saving ? 'Saving…' : 'Save Billing Details'}
        </button>
      </div>
    </div>
  )
}
